import { useState, useCallback, useRef, useEffect } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import guideAIChatApi, {
  ChatMessageRequest,
  ChatMessageResponse,
  ChatHistoryMessage,
  FileAttachment,
} from '@/services/apis/guide-ai-chat';
import { guideKeys } from './useGuides';
import { GuideBuilderFormData } from '@/components/guide-builder/schemas';

export interface ChatMessage extends ChatHistoryMessage {
  isStreaming?: boolean;
}

export interface Checkpoint {
  guide: GuideBuilderFormData;
  timestamp: number;
}

export const useGuideAIChat = (guideId: string, currentGuide: GuideBuilderFormData) => {
  const queryClient = useQueryClient();
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [isStreaming, setIsStreaming] = useState(false);
  const [focusedStepId, setFocusedStepId] = useState<string | undefined>(undefined);
  const checkpointsRef = useRef<Checkpoint[]>([]);

  // Create or get session
  const { data: sessionData, isLoading: isSessionLoading } = useQuery({
    queryKey: ['guide-chat-session', guideId],
    queryFn: async () => {
      const response = await guideAIChatApi.createSession(guideId);
      if (response.data) {
        return response.data;
      }
      throw new Error('Failed to create session');
    },
    enabled: !!guideId,
    staleTime: Infinity,
  });

  const sessionId = sessionData?.sessionId;
  // Load session history if session exists
  const { data: historyData } = useQuery({
    queryKey: ['guide-chat-history', guideId, sessionId],
    queryFn: async () => {
      if (!sessionId) return null;
      const response = await guideAIChatApi.getSessionHistory(guideId, sessionId);
      return response.data;
    },
    enabled: !!sessionId,
    staleTime: Infinity,
  });

  // Update messages when history is loaded
  useEffect(() => {
    if (historyData?.messages) {
      setMessages(
        historyData.messages.map((msg: ChatHistoryMessage) => ({
          ...msg,
          isStreaming: false,
        })),
      );
    }
  }, [historyData]);

  // Save checkpoint before AI update
  const saveCheckpoint = useCallback(() => {
    checkpointsRef.current.push({
      guide: JSON.parse(JSON.stringify(currentGuide)), // Deep clone
      timestamp: Date.now(),
    });
    // Keep only last 10 checkpoints
    if (checkpointsRef.current.length > 10) {
      checkpointsRef.current.shift();
    }
  }, [currentGuide]);

  // Undo last change
  const undo = useCallback(() => {
    const checkpoint = checkpointsRef.current.pop();
    if (checkpoint) {
      // Invalidate guide query to trigger refetch
      queryClient.invalidateQueries({ queryKey: guideKeys.detail(guideId) });
      // Note: The actual restoration will happen when guide data is refetched
      // This is a simplified approach - in a real implementation, you might want
      // to directly update the guide state
      return true;
    }
    return false;
  }, [guideId, queryClient]);

  // Send message mutation
  const sendMessageMutation = useMutation({
    mutationFn: async (request: ChatMessageRequest & { displayFileAttachments?: FileAttachment[] }) => {
      if (!sessionId) {
        throw new Error('Session not initialized');
      }

      // Save checkpoint before AI update
      saveCheckpoint();

      // Add user message with display file attachments
      const userMessage: ChatMessage = {
        id: `user-${Date.now()}`,
        role: 'user',
        content: request.message,
        fileAttachments: request.displayFileAttachments,
        focusedStepId: request.focusedStepId,
        createdAt: new Date().toISOString(),
      };

      setMessages((prev) => [...prev, userMessage]);
      setIsStreaming(true);

      // Create assistant message placeholder
      const assistantMessageId = `assistant-${Date.now()}`;
      const assistantMessage: ChatMessage = {
        id: assistantMessageId,
        role: 'assistant',
        content: '',
        isStreaming: true,
        createdAt: new Date().toISOString(),
      };

      setMessages((prev) => [...prev, assistantMessage]);

      // Stream response
      let fullContent = '';
      let guideUpdates: ChatMessageResponse['guideUpdates'] = undefined;

      try {
        // Send only the API-compatible request (without displayFileAttachments)
        const apiRequest: ChatMessageRequest = {
          message: request.message,
          sessionId: request.sessionId,
          focusedStepId: request.focusedStepId,
          fileIds: request.fileIds,
        };
        const stream = guideAIChatApi.sendMessage(guideId, apiRequest);

        for await (const chunk of stream) {
          // Check if this is an error response
          if (chunk.error) {
            fullContent = chunk.content || 'An error occurred';
            setIsStreaming(false);
            setMessages((prev) =>
              prev.map((msg) =>
                msg.id === assistantMessageId
                  ? {
                      ...msg,
                      content: fullContent,
                      isStreaming: false,
                    }
                  : msg,
              ),
            );
            break;
          }

          fullContent += chunk.content || '';
          if (chunk.guideUpdates) {
            guideUpdates = chunk.guideUpdates;
          }

          // Update streaming message
          setMessages((prev) =>
            prev.map((msg) =>
              msg.id === assistantMessageId
                ? {
                    ...msg,
                    content: fullContent,
                    isStreaming: !chunk.isComplete,
                    guideUpdates: guideUpdates
                      ? {
                          type: guideUpdates[0]?.type || 'guide',
                          changes: guideUpdates[0]?.changes || {},
                        }
                      : undefined,
                  }
                : msg,
            ),
          );

          // If guide updates are received, invalidate guide query
          if (chunk.guideUpdates && chunk.guideUpdates.length > 0) {
            queryClient.invalidateQueries({ queryKey: guideKeys.detail(guideId) });
          }

          if (chunk.isComplete) {
            setIsStreaming(false);
            break;
          }
        }
      } catch (error) {
        setIsStreaming(false);
        const errorMessage = error instanceof Error ? error.message : 'Failed to get response from AI.';
        setMessages((prev) =>
          prev.map((msg) =>
            msg.id === assistantMessageId
              ? {
                  ...msg,
                  content: msg.content || `Error: ${errorMessage}`,
                  isStreaming: false,
                }
              : msg,
          ),
        );
        // Don't throw - error is already displayed in the message
      }

      return { fullContent, guideUpdates };
    },
    onSuccess: () => {
      // Invalidate history to sync with backend
      queryClient.invalidateQueries({ queryKey: ['guide-chat-history', guideId, sessionId] });
    },
  });

  const sendMessage = useCallback(
    (message: string, fileAttachments?: FileAttachment[], stepId?: string) => {
      if (!message.trim() && !fileAttachments?.length) return;
      if (isStreaming) return;

      setFocusedStepId(stepId);

      sendMessageMutation.mutate({
        message: message.trim() || '',
        sessionId: sessionId || undefined,
        focusedStepId: stepId,
        fileIds: fileAttachments?.map((att) => att.id),
        displayFileAttachments: fileAttachments, // Keep for UI display
      });
    },
    [isStreaming, sessionId, sendMessageMutation],
  );

  return {
    messages,
    isStreaming,
    sessionId: sessionId || sessionData?.sessionId,
    isSessionLoading,
    focusedStepId,
    setFocusedStepId,
    sendMessage,
    undo,
    canUndo: checkpointsRef.current.length > 0,
  };
};
